import { Head } from "@inertiajs/react";
import { salonpageR2ImageUrl, encodeSalonpagePathId } from "@/lib/_url";
import { SalonpageFooter } from "./_footer";
import { NoroshiReserveCta } from "./_noroshi-reserve-cta";

interface Salon {
  id: string;
  name: string;
  nameKana: string;
}

interface StyleItem {
  id: string;
  name: string;
  staffName: string;
  imageUrl: string;
  styleUrl: string;
  position: number;
}

interface Meta {
  title: string;
  description: string;
}

interface FeatureToggles {
  noroshiReserve?: boolean | number | null;
}

interface Props {
  salon?: Salon;
  style?: StyleItem | null;
  relatedStyles?: StyleItem[];
  featureToggles?: FeatureToggles;
  meta?: Meta;
}

export default function SalonStyle(props: Props) {
  if (!props.salon || !props.style) return <NotFound label="Style not found." />;

  const salon = props.salon;
  const style = props.style;
  const relatedStyles = props.relatedStyles ?? [];
  const meta = props.meta ?? { title: style.name, description: `${salon.name}のヘアスタイル詳細です。` };

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="min-h-screen bg-gray-100 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] rounded-b-[22px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.06)]">
          <header className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur">
            <a href={`/salons/${encodeSalonpagePathId(salon.id)}/styles`} className="text-xs font-bold text-zinc-400">← ヘアスタイル一覧へ</a>
          </header>

          <img src={salonpageR2ImageUrl(salon.id, style.id, "gallery")} alt={style.name} className="aspect-[3/4] w-full bg-gray-100 object-cover" />

          <section className="space-y-4 p-4">
            <div>
              <p className="text-xs font-bold text-zinc-400">{salon.name}</p>
              <h1 className="mt-2 text-2xl font-bold leading-8 tracking-tight">{style.name}</h1>
              {style.staffName && <p className="mt-2 text-sm font-medium text-zinc-500">担当: {style.staffName}</p>}
            </div>

            <NoroshiReserveCta
              salonId={salon.id}
              enabled={props.featureToggles?.noroshiReserve}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[#070a18] px-4 text-sm font-bold text-white"
            >
              Web予約する
            </NoroshiReserveCta>
          </section>

          {relatedStyles.length > 0 && (
            <section className="border-t border-gray-100 p-4">
              <h2 className="mb-4 text-xl font-bold tracking-tight">ほかのヘアスタイル</h2>
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {relatedStyles.map((item) => (
                  <a key={item.id} href={styleDetailUrl(salon, item)} className="w-32 shrink-0">
                    <img src={salonpageR2ImageUrl(salon.id, item.id, "thumbnail")} alt={item.name} className="aspect-[3/4] w-full rounded-md bg-gray-100 object-cover" />
                    <p className="mt-2 line-clamp-2 text-xs font-bold leading-5">{item.name}</p>
                  </a>
                ))}
              </div>
            </section>
          )}

        </main>
        <SalonpageFooter salon={salon} current="styles" />
      </div>
    </>
  );
}

function NotFound({ label }: { label: string }) {
  return (
    <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
      <p className="text-sm text-zinc-400">{label}</p>
    </main>
  );
}

function styleDetailUrl(salon: Salon, style: StyleItem) {
  return `/salons/${encodeSalonpagePathId(salon.id)}/styles/${encodeSalonpagePathId(style.id)}`;
}
